import { useState, useRef, useEffect, useMemo, type ReactNode } from "react";
import { ChevronDown, Search, Check } from "./icons.js";

export const DEPARTMENT_OPTIONS = [
  "Engineering",
  "Quality Assurance & Testing",
  "Technology & Architecture",
  "Product Management",
  "Design & UX",
  "Data Science & Analytics",
  "Sales & Business Development",
  "Marketing & Growth",
  "Customer Support & Operations",
  "Human Resources & Talent",
  "Finance & Accounting",
  "Legal & Compliance",
  "Healthcare & Clinical Care",
  "Operations & Supply Chain",
];

export const INDUSTRY_OPTIONS = [
  "Technology & Software",
  "Enterprise Software / SaaS",
  "Financial Services / FinTech",
  "Healthcare & Life Sciences",
  "E-Commerce & Retail",
  "AI & Data Platforms",
  "Telecommunications & Media",
  "Automotive & Mobility",
  "Real Estate & PropTech",
  "Education & EdTech",
  "Manufacturing & Industrial",
  "Consulting & Professional Services",
];

export const NOTICE_PERIOD_OPTIONS = [
  "Immediate (Ready to join)",
  "15 Days or less",
  "30 Days (1 Month)",
  "45 Days",
  "60 Days (2 Months)",
  "90 Days (3 Months)",
  "Immediate to 30 days",
  "Serving notice (Active buyout)",
];

interface Props {
  options: string[];
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  allowCustom = true,
  className,
  style,
}: Props): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    inputRef.current?.focus();
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  const isCustom =
    query.trim().length > 0 &&
    !options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  return (
    <div
      ref={containerRef}
      className={`searchable-select-root ${className ?? ""}`}
      style={{ position: "relative", width: "100%", ...style }}
    >
      <div
        className={`searchable-select-trigger${open ? " open" : ""}${value ? "" : " empty"}`}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        <span className="searchable-select-value">{value || placeholder}</span>
        <ChevronDown className="searchable-select-chevron" />
      </div>

      {open && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <Search className="searchable-select-search-icon" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search or type custom…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filteredOptions.length > 0 && filteredOptions[0]) {
                    handleSelect(filteredOptions[0]);
                  } else if (allowCustom && query.trim()) {
                    handleSelect(query.trim());
                  }
                } else if (e.key === "Escape") {
                  setOpen(false);
                }
              }}
            />
          </div>

          <div className="searchable-select-list" role="listbox">
            {allowCustom && isCustom && (
              <div className="searchable-select-opt custom" onClick={() => handleSelect(query.trim())}>
                + Use &ldquo;{query.trim()}&rdquo;
              </div>
            )}
            {filteredOptions.map((opt) => {
              const isSelected = opt === value;
              return (
                <div
                  key={opt}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(opt)}
                  className={`searchable-select-opt${isSelected ? " selected" : ""}`}
                >
                  <span>{opt}</span>
                  {isSelected && <Check style={{ width: 14, height: 14, flexShrink: 0 }} />}
                </div>
              );
            })}
            {filteredOptions.length === 0 && !isCustom && (
              <div className="searchable-select-none">
                No matches found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
